import React, { useContext, useMemo } from 'react';
import ENDPOINT from '../../../../constants/endpoints';
import AsyncSelectPane from '../../../../GbsUI/components/AsyncSelectPane';
import Checkbox from '../../../../GbsUI/UI/Checkbox';
import Select from '../../../../GbsUI/UI/Select';
import { useTaxonomy } from '../../../../hooks/useTaxonomy';
import { MONTHS_OPTIONS, YEAR_OPTIONS } from '../../constants';
import { SignupContext } from '../../context';

const ExperienceItem = ({
  experience,
  index,
  errors,
  titles,
  titlesLoading,
  roles,
  rolesLoading,
  industries,
  industriesLoading,
}) => {
  const {state, setState} = useContext(SignupContext);
  const selectedRole = useMemo(() => experience?.role?.id, [experience?.role]);
  const {data: groups, isLoading: groupsLoading} = useTaxonomy('GROUPS', {role: selectedRole});

  return (
    <div className={'signup-form-row'}>
      <div className={'signup-form-item signup-form-item--full'}>
        <AsyncSelectPane
          label={'Firm'}
          title={'Change your firm'}
          selected={experience.firm}
          name={`items[${index}].firm`}
          errors={errors.get(`items[${index}].firm`)}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      firm: v,
                    }
                    : item
                ),
              },
            });
          }}
          source={ENDPOINT.TAXONOMIES.FIRMS}
          createNewTitle={"Can't find your firm in the list?"}
          createNewSubtitle={'Manually add your firm in the field below.'}
          createNewPlaceholder={'e.g. Goldman Sachs'}
          notFoundText={'Firm is not found'}
          searchPlaceholder={'Enter the firm name'}
          help={
            'Indicate the firm you have joined or are joining. Full-time ongoing employment offers only - no internships or temp roles.'
          }
          creatable
          autoselect={true}
          disclaimer={
            'Note: GoBuyside has no affiliation with these firms; names and logos are displayed for informational purposes and clarity.'
          }
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <Select
          label={'Position'}
          name={`items[${index}].position`}
          help={
            'Titles can differ between companies. Please select the title that most accurately reflects your position.'
          }
          options={titles}
          isLoading={titlesLoading}
          value={experience?.position || ''}
          errors={errors.get(`items[${index}].position`)}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      position: v,
                    }
                    : item
                ),
              },
            });
          }}
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <Select
          label={'Role'}
          options={roles}
          isLoading={rolesLoading}
          value={experience?.role || ''}
          name={`items[${index}].role`}
          errors={errors.get(`items[${index}].role`)}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      role: v,
                      group: [],
                    }
                    : item
                ),
              },
            });
          }}
        />
      </div>
      {groups.length > 0 && (
        <div className={'signup-form-item signup-form-item--full'}>
          <Select
            label={'Group'}
            options={groups}
            isLoading={groupsLoading}
            value={experience?.group}
            name={`items[${index}].group`}
            errors={errors.get(`items[${index}].group`)}
            isMulti
            onChange={v => {
              setState({
                ...state,
                experience: {
                  ...state.experience,
                  items: state.experience.items.map((item, i) =>
                    i === index
                      ? {
                        ...item,
                        group: v,
                      }
                      : item
                  ),
                },
              });
            }}
          />
        </div>
      )}
      <div className={'signup-form-item signup-form-item--full'}>
        <Select
          label={'Industry'}
          options={industries}
          name={`items[${index}].industry`}
          errors={errors.get(`items[${index}].industry`)}
          isLoading={industriesLoading}
          value={experience?.industry}
          isMulti
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      industry: v,
                    }
                    : item
                ),
              },
            });
          }}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          label={'Start Month'}
          help={
            'Even if you have received a promotion since starting at the firm, enter the month and year you started at the firm.'
          }
          options={MONTHS_OPTIONS}
          name={`items[${index}].startMonth`}
          errors={errors.get(`items[${index}].startMonth`)}
          value={experience?.startMonth}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      startMonth: v,
                    }
                    : item
                ),
              },
            });
          }}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          label={'Start Year'}
          options={YEAR_OPTIONS}
          value={experience?.startYear}
          name={`items[${index}].startYear`}
          errors={errors.get(`items[${index}].startYear`)}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      startYear: v,
                    }
                    : item
                ),
              },
            });
          }}
        />
      </div>
      {!experience.present ? (
        <>
          <div className={'signup-form-item'}>
            <Select
              label={'End Month'}
              help={
                'Even if you have received a promotion since starting at the firm, enter the month and year you started at the firm.'
              }
              options={MONTHS_OPTIONS}
              name={`items[${index}].endMonth`}
              errors={errors.get(`items[${index}].endMonth`)}
              value={experience?.endMonth}
              onChange={v => {
                setState({
                  ...state,
                  experience: {
                    ...state.experience,
                    items: state.experience.items.map((item, i) =>
                      i === index
                        ? {
                          ...item,
                          endMonth: v,
                        }
                        : item
                    ),
                  },
                });
              }}
            />
          </div>
          <div className={'signup-form-item'}>
            <Select
              label={'End Year'}
              options={YEAR_OPTIONS}
              value={experience?.endYear}
              name={`items[${index}].endYear`}
              errors={errors.get(`items[${index}].endYear`)}
              onChange={v => {
                setState({
                  ...state,
                  experience: {
                    ...state.experience,
                    items: state.experience.items.map((item, i) =>
                      i === index
                        ? {
                          ...item,
                          endYear: v,
                        }
                        : item
                    ),
                  },
                });
              }}
            />
          </div>
        </>
      ) : null}
      <div className={'signup-form-item signup-form-item--full'}>
        <Checkbox
          name={`[${index}].present`}
          className={'student-form__firm-checkbox'}
          onChange={e => {
            const target = e.target;

            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      present: target.checked,
                      endMonth: null,
                      endYear: null,
                    }
                    : item
                ),
              },
            });
          }}
          label={'Currently Employed'}
          checked={!!experience.present}
        />
      </div>
      <div className={'signup-form-item'}>
        <AsyncSelectPane
          label={'Office location'}
          name={`items[${index}].city`}
          errors={errors.get(`items[${index}].city`)}
          searchHelp={'If your office location is not listed, begin typing to search for it.'}
          title={'Change your office location'}
          selected={experience?.city}
          onChange={v => {
            setState({
              ...state,
              experience: {
                ...state.experience,
                items: state.experience.items.map((item, i) =>
                  i === index
                    ? {
                      ...item,
                      city: v,
                    }
                    : item
                ),
              },
            });
          }}
          source={ENDPOINT.TAXONOMIES.CITIES}
          notFoundText={'City is not found'}
          searchPlaceholder={'Enter the city name'}
          showIcon={false}
          autoselect
        />
      </div>
    </div>
  );
};

export default ExperienceItem;
